/**
 * 닉네임 정책 — 웹 폼·zod 스키마·RoomDO가 공용으로 사용 (단일 출처).
 * - 1~15자
 * - 욕설·금지어 포함 불가 (공백/특수문자 끼워넣기 우회도 정규화로 차단)
 */

export const NICKNAME_MAX = 10;
export const NICKNAME_MIN = 1;

/**
 * 금지어 — 필요 시 여기에만 추가 (부분 일치 차단이라 '좆'은 '좆같네' 등 파생어도 커버).
 * 공개 한국어 비속어 필터 목록(badwords-ko, korean_profanity_filter 등)을 참고해 선별.
 * ⚠️ 일상어와 겹치는 짧은 단어는 오탐이 나므로 넣지 않는다 (예: '년', '개' 단독)
 */
const BANNED_WORDS = [
  // 욕설·비속어 (한글)
  '시발',
  '씨발',
  '씨빨',
  '싀발',
  '시팔',
  '씨팔',
  '쉬발',
  '쓰발',
  '스발',
  '샤발',
  '씨바',
  '시바',
  'ㅅㅂ',
  'ㅆㅂ',
  'ㅆㅃ',
  'ㅄ',
  'ㅂㅅ',
  'ㅈㄹ',
  'ㅈ같',
  '썅',
  '병신',
  '븅신',
  '빙신',
  '등신',
  '머저리',
  '지랄',
  '찌랄',
  '존나',
  '졸라',
  '조낸',
  '좆',
  '좃',
  '조까',
  '족까',
  '꺼져',
  '개새',
  '개색',
  '개자식',
  '개소리',
  '개넘',
  '개년',
  '개놈',
  '새끼',
  '새꺄',
  '색기',
  '색끼',
  '쉐끼',
  '니미',
  '니애미',
  '니앰',
  '느그애미',
  '느금',
  '애미뒤진',
  '니미럴',
  '엠창',
  '앰창',
  '미친놈',
  '미친년',
  '미친새',
  '또라이',
  '돌아이',
  '싸이코',
  '쌍놈',
  '쌍년',
  '상놈',
  '후레자식',
  '후레아들',
  '걸레',
  '창녀',
  '창년',
  '창놈',
  '갈보',
  '화냥',
  // 성적 표현
  '자지',
  '보지',
  '씹',
  '섹스',
  '성교',
  '야동',
  '야사',
  '딸딸이',
  '자위',
  '불알',
  '고환',
  '후장',
  '떡치',
  // 비하·혐오
  '찐따',
  '애자',
  '장애년',
  '장애놈',
  '틀딱',
  '급식충',
  '한남충',
  '김치녀',
  '된장녀',
  '짱깨',
  '쪽바리',
  '흑형',
  // 타자 우회 (한글 발음의 영타 입력)
  'tlqkf',
  'Tlqkf',
  'qudtls',
  'wlfkf',
  'tqkf',
  'anfclsu',
  // 영어
  'fuck',
  'fck',
  'fuk',
  'shit',
  'bitch',
  'asshole',
  'bastard',
  'dick',
  'pussy',
  'cunt',
  'nigger',
  'nigga',
  'faggot',
  'porn',
  'sex',
  // 사칭 방지
  '운영자',
  '관리자',
  '타로위드공식',
  'admin',
  'administrator',
  'official',
] as const;

/** 우회 입력(공백·기호 삽입, 대문자) 정규화 */
const normalize = (s: string) => s.toLowerCase().replace(/[\s\-_.·~!@#$%^&*()+=,?'"|/\\]/g, '');

export function findBannedWord(name: string): string | null {
  const n = normalize(name);
  return BANNED_WORDS.find((w) => n.includes(w)) ?? null;
}

/** 가입 시 자동 세팅되는 기본 닉네임 — 밤하늘/타로 무드 (항상 NICKNAME_MAX 이내) */
const NICK_ADJ = [
  '달빛',
  '별빛',
  '새벽',
  '금빛',
  '보랏빛',
  '안개숲',
  '호수의',
  '바람의',
  '꿈꾸는',
  '구름위',
];
const NICK_NOUN = [
  '여우',
  '고양이',
  '부엉이',
  '토끼',
  '사슴',
  '나비',
  '고래',
  '늑대',
  '문지기',
  '점술사',
];

export function randomNickname(): string {
  const r = crypto.getRandomValues(new Uint8Array(2));
  return `${NICK_ADJ[r[0]! % NICK_ADJ.length]}${NICK_NOUN[r[1]! % NICK_NOUN.length]}`;
}

export type NicknameCheck = { ok: true; value: string } | { ok: false; reason: string };

export function validateNickname(raw: string): NicknameCheck {
  const value = raw.trim();
  if (value.length < NICKNAME_MIN) return { ok: false, reason: '닉네임을 입력해 주세요' };
  if (value.length > NICKNAME_MAX)
    return { ok: false, reason: `닉네임은 ${NICKNAME_MAX}자 이하로 지어주세요` };
  if (findBannedWord(value)) return { ok: false, reason: '사용할 수 없는 단어가 들어있어요' };
  return { ok: true, value };
}
